import {
  AGENCY_EXTRA_BRAND_PRICE,
  BILLING_CURRENCIES,
  PLAN_PRICES,
  type BillingCurrency,
  type PlanPrice,
} from '@unipostudio/platform-constants';

export type BillingInterval = 'monthly' | 'yearly';

/** A billing currency from an API string, EUR for anything unknown. */
export function toBillingCurrency(raw: string | null | undefined): BillingCurrency {
  const upper = (raw ?? '').toUpperCase();
  return (BILLING_CURRENCIES as readonly string[]).includes(upper) ? (upper as BillingCurrency) : 'EUR';
}

/** The interval a subscription is billed on, monthly unless it says yearly. */
export function toBillingInterval(raw: string | null | undefined): BillingInterval {
  return (raw ?? '').toLowerCase() === 'yearly' ? 'yearly' : 'monthly';
}

/**
 * The price of one Agency brand above the included five (ADR-006). The
 * server's quote (EXTRA_BRAND_REQUIRED → details.pricePerBrand) wins when it
 * has one for the currency; the shared price table is the fallback, and is
 * what the server quotes from.
 */
export function extraBrandUnit(
  currency: BillingCurrency,
  interval: BillingInterval,
  quoted?: Partial<Record<string, Partial<PlanPrice>>> | null,
): number {
  const fromServer = quoted?.[currency]?.[interval];
  if (typeof fromServer === 'number') return fromServer;
  return AGENCY_EXTRA_BRAND_PRICE[currency][interval];
}

/**
 * Extra brands an Agency account pays for once it holds `brandsAfter` brands:
 * every brand above the included ones (ADR-006). Counted from the brands it
 * will hold, never from the extras already paid plus one: an account holding
 * more brands than it pays for is billed for all of them.
 */
export function extraBrandsNeeded(brandsAfter: number, included: number): number {
  return Math.max(0, brandsAfter - included);
}

/** The extra-brand price the server quoted, in the currency the account is charged in. */
export interface ExtraBrandQuote {
  /** ISO currency the account is billed in; never a guess. */
  currency: string;
  interval: BillingInterval;
  /** Extra brands paid for once the new brand exists. */
  extrasAfter: number;
  /** Price of one extra brand per interval. */
  perBrand: number;
  /** Agency plus every extra brand, per interval. */
  total: number;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

/**
 * Reads the server's quote from EXTRA_BRAND_REQUIRED details. It is a quote
 * only when the server names the currency the account can be charged in and
 * a price per brand in it; anything else is null, and no price is shown.
 * The API sends `details.quote { currency, interval, unitAmount, quantity,
 * total, purchasable, adminManaged }` (BILLING.md §11.12); a quote that is not
 * purchasable, or an admin-managed account, is no quote. Also accepted: the
 * flat fields (`currency`, `interval`, `pricePerBrand[currency]`,
 * `newTotal`, `extraBrandsAfter`/`quantity`). Without a server total, the
 * total is Agency in that currency plus the extras.
 */
export function serverExtraBrandQuote(
  details: Record<string, unknown>,
  fallback: { interval: BillingInterval; extrasAfter: number },
): ExtraBrandQuote | null {
  const q = (details['quote'] && typeof details['quote'] === 'object' ? details['quote'] : {}) as Record<string, unknown>;
  if (q['purchasable'] === false || q['adminManaged'] === true) return null;
  const currency = str(q['currency']) ?? str(details['currency']);
  if (!currency) return null;
  const rawInterval = str(q['interval']) ?? str(details['interval']);
  const interval: BillingInterval = rawInterval ? toBillingInterval(rawInterval) : fallback.interval;
  const extrasAfter =
    num(q['extraBrandsAfter']) ?? num(q['quantity']) ?? num(details['extraBrandsAfter']) ?? num(details['quantity']) ?? fallback.extrasAfter;
  const table = details['pricePerBrand'] && typeof details['pricePerBrand'] === 'object'
    ? (details['pricePerBrand'] as Record<string, Partial<PlanPrice> | undefined>)
    : {};
  const perBrand = num(q['unitAmount']) ?? num(q['perBrand']) ?? num(q['unitPrice']) ?? num(table[currency.toUpperCase()]?.[interval]);
  if (perBrand === null) return null;
  const known = (BILLING_CURRENCIES as readonly string[]).includes(currency.toUpperCase());
  const total =
    num(q['total']) ??
    num(q['newTotal']) ??
    num(details['newTotal']) ??
    (known ? PLAN_PRICES.AGENCY[currency.toUpperCase() as BillingCurrency][interval] + extrasAfter * perBrand : null);
  if (total === null) return null;
  return { currency: currency.toUpperCase(), interval, extrasAfter, perBrand, total };
}

/** Agency plus `extraBrands` paid extras, per interval, in `currency`. */
export function agencyTotal(
  currency: BillingCurrency,
  interval: BillingInterval,
  extraBrands: number,
  quoted?: Partial<Record<string, Partial<PlanPrice>>> | null,
): number {
  return PLAN_PRICES.AGENCY[currency][interval] + Math.max(0, extraBrands) * extraBrandUnit(currency, interval, quoted);
}
